import React, { useEffect, useState } from 'react';
import { FiUser } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../../../../store/languageSlice';
import Dropdown from '../../../UI/Dropdown/Dropdown';
import closeOpenedDropdown from '../../../../utilFunctions/DropdownCloser';
import { getTValue } from '../../../../translations/common';

const EditGuardian = ({ data, onAddGuadrian, patientsRelations }) => {
  const { t, isRtl } = useSelector(selectLanguage);

  const [isDurationInputVisible, setIsDurationInputVisible] = useState(false);
  const [mrNumber, setMrNumber] = useState('');
  const [noOfDays, setNoOfDays] = useState(0);
  const [type, setType] = useState('add');
  const [setectedRelation, setSetectedRelation] = useState();
  const [isRelationDisabled, setRelationDisabled] = useState(false);

  const [errors, setErrors] = useState({
    mrNumber: '',
    relation: '',
    noOfDays: '',
  });

  const handleRadioChange = (isNumberOfDays) => {
    setIsDurationInputVisible(isNumberOfDays);
    setNoOfDays(0);

    setErrors((prev) => ({
      ...prev,
      noOfDays: '',
    }));
  };

  const handleMrNumberChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');

    setMrNumber(value);

    if (value) {
      setErrors((prev) => ({
        ...prev,
        mrNumber: '',
      }));
    }
  };

  const handleDaysChange = (event) => {
    let value = event.target.value.replace(/\D/g, '');

    if (value.length > 2) {
      value = value.slice(0, 2);
    }

    // Allow empty value while typing/backspacing
    setNoOfDays(value === '' ? '' : Number(value));

    if (Number(value) > 0) {
      setErrors((prev) => ({
        ...prev,
        noOfDays: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      mrNumber: '',
      relation: '',
      noOfDays: '',
    };

    let isValid = true;

    // MR Number validation
    if (!mrNumber || !mrNumber.trim()) {
      newErrors.mrNumber = t.MrnIsRequired;
      isValid = false;
    }

    // Relation validation
    if (type !== 'edit' && !setectedRelation?.relationCode) {
      newErrors.relation = t.SelectRelationToPatient;
      isValid = false;
    }

    // Number of Days validation
    if (isDurationInputVisible) {
      if (!noOfDays || Number(noOfDays) <= 0) {
        newErrors.noOfDays = t.InvalidNoOfDays;
        isValid = false;
      }
    }

    setErrors(newErrors);

    return isValid;
  };

  const handleAddButtonClick = () => {
    if (!validateForm()) {
      return;
    }

    onAddGuadrian &&
      onAddGuadrian({
        type,
        mrNumber,
        noOfDays: isDurationInputVisible ? Number(noOfDays) : 0,
        guardianRelation: setectedRelation?.relationCode || '',
      });
  };

  const changeOptionsHandler = (selected) => {
    setSetectedRelation(selected);

    setErrors((prev) => ({
      ...prev,
      relation: '',
    }));

    closeOpenedDropdown();
  };

  useEffect(() => {
    if (data && data?.guardianId) {
      if (data?.noOfDays > 0) {
        setIsDurationInputVisible(true);
      }

      setNoOfDays(data?.noOfDays || 0);
      setMrNumber(data?.guardianId);

      const code = data.userDetails?.guardianRelation;

      const relationItem = patientsRelations?.find(
        (x) => x.relationCode == code
      );

      if (relationItem) {
        setSetectedRelation(relationItem);
      }
      setRelationDisabled(true)

      setType('edit');
    }
  }, [data, patientsRelations]);

  return (
    <div className="border bg-white shadow rounded-md p-8 max-w-md w-full mx-auto">
      <div className="flex items-center space-x-4 gap-y-2.5">
        <div className="w-8 h-8 rounded-full bg-gray-200 flex justify-center items-center">
          <FiUser className="w-6 h-6" />
        </div>

        <h1 className="mt-1 text-xl text-grey-700">
          {data?.userDetails ? t.Update_A_Guardian : t.Add_A_Guardian}
        </h1>
      </div>

      <div className="w-full mt-5">
        <div className="flex flex-col gap-2 ml-12">

          {/* MR NUMBER */}
          <label
            htmlFor="mrNumber"
            className="text-base font-extrabold leading-6 text-gray-900 float-left"
          >
            {t.MR_Number}
          </label>

          <div>
            <input
              type="text"
              id="mrNumber"
              name="mrNumber"
              value={mrNumber || ''}
              disabled={type === 'edit'}
              onChange={handleMrNumberChange}
              className={`block w-50 rounded-md border-0 py-1.5 px-4 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                errors.mrNumber
                  ? 'ring-red-500 focus:ring-red-500'
                  : 'focus:ring-indigo-600'
              }`}
              placeholder={t.Enter_MR_Number}
            />

            {errors.mrNumber && (
              <p className="text-red-500 text-xs mt-1">
                {errors.mrNumber}
              </p>
            )}
          </div>

          {/* RELATION */}
          <div>
            <Dropdown extraClass="site-switer">
              <div>
                <input
                  type="text"
                  id="relation"
                  name="relation"
                  readOnly
                  value={
                    isRtl
                      ? setectedRelation?.relationNameAr || ''
                      : setectedRelation?.relationName || ''
                  }
                  className={`block w-50 rounded-md border-0 py-1.5 px-4 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 cursor-pointer ${
                    errors.relation
                      ? 'ring-red-500 focus:ring-red-500'
                      : 'focus:ring-indigo-600'
                  }`}
                  placeholder={t.SelectRelation}
                  disabled={isRelationDisabled}
                />
              </div>

              <div
                className="dd-menu !px-0"
                style={{
                  minWidth: '188px',
                  right: 'auto',
                }}
              >
                <ul>
                  {patientsRelations?.map((item, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        className={
                          setectedRelation?.relationCode === item.relationCode
                            ? 'active !px-3'
                            : '!px-3 text-grey-700'
                        }
                        onClick={() => changeOptionsHandler(item)}
                      >
                        {getTValue(
                          item,
                          'relationNameAr',
                          'relationName',
                          isRtl
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </Dropdown>

            {errors.relation && (
              <p className="text-red-500 text-xs mt-1">
                {errors.relation}
              </p>
            )}
          </div>

          <h1 className="font-extrabold text-base">
            {t.Duration_Of_Guardian}
          </h1>

          {/* UNLIMITED */}
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="unlimited"
              name="guardianDuration"
              checked={!isDurationInputVisible}
              onChange={() => handleRadioChange(false)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
            />

            <label
              htmlFor="unlimited"
              className="!m-0 text-sm font-medium text-gray-900 dark:text-black"
            >
              {t.Unlimited}
            </label>
          </div>

          {/* NUMBER OF DAYS */}
          <div className="flex items-start gap-2">
            <input
              type="radio"
              id="numberOfDays"
              name="guardianDuration"
              checked={isDurationInputVisible}
              onChange={() => handleRadioChange(true)}
              className="w-4 h-4 mt-[2px] text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
            />

            {isDurationInputVisible ? (
              <div className="w-35">
                <input
                  type="text"
                  inputMode="numeric"
                  id="numberOfDaysInput"
                  name="numberOfDaysInput"
                  value={noOfDays}
                  maxLength={2}
                  className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                    errors.noOfDays
                      ? 'ring-red-500 focus:ring-red-500'
                      : 'focus:ring-indigo-600'
                  }`}
                  placeholder={t.Enter_Number_of_Days}
                  onChange={handleDaysChange}
                />

                <div className="flex justify-between mt-1">
                  {errors.noOfDays ? (
                    <p className="text-red-500 text-xs">
                      {errors.noOfDays}
                    </p>
                  ) : (
                    <span />
                  )}

                  <span className="text-xs text-gray-500">
                    {String(noOfDays || 0).length}/2
                  </span>
                </div>
              </div>
            ) : (
              <label
                htmlFor="numberOfDays"
                className="!m-0 text-sm font-medium text-gray-900 dark:text-black"
              >
                {t.Number_of_Days}
              </label>
            )}
          </div>
        </div>

        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={handleAddButtonClick}
            className="btn bg-sky-400 p-2 rounded text-white w-20"
          >
            {data?.userDetails ? t.Update : t.Add}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditGuardian;